import { Heading2, Heading3, Paragraph1, SmallText } from 'inpock-ui-react';
import { colors } from 'inpock-ui-react/dist/src/color';

import { Block } from '@/apis';
import { Accordion, Box, Icon, ReviewRating } from '@/components';
import { CATEGORY_LIST, CONTENT_CATEGORY } from '@/constants';

import * as S from './styled';

interface CampaignContentProps {
  block: Block;
  onClick: () => void;
}

function InnerBox({ children }: { children: React.ReactNode }) {
  return (
    <Box
      flex
      flexDirection="column"
      sx={{
        padding: '14px 18px',
        borderRadius: '8px',
        backgroundColor: colors.gray100,
        marginBottom: '10px',
        gap: '14px',
      }}
    >
      {children}
    </Box>
  );
}

export function CampaignContent({ block, onClick }: CampaignContentProps) {
  const { campaign } = block;

  const categoryPrimary = campaign.category.primary;
  const selectedCategory = CATEGORY_LIST[categoryPrimary];
  const index = selectedCategory.value.findIndex((v: string) => v === campaign.category.secondary);

  return (
    <section className="mt-16">
      <SmallText
        style={{ fontWeight: 300, display: 'flex', alignItems: 'center', gap: '4px' }}
        className="text-gray-400 mb-4"
      >
        {CONTENT_CATEGORY[campaign?.category.primary]}{' '}
        <Icon icon="ArrowRight" viewBox="0.5 0 12.5 12" color="#d8d8d8" size={6} />
        {selectedCategory.label[index]}
      </SmallText>
      <Paragraph1 fontWeight="regular" className="mb-8" style={{ letterSpacing: '-0.48px' }}>
        {campaign?.name}
      </Paragraph1>
      <ReviewRating
        average={campaign?.review_data.avr_rating.toLocaleString() || '0'}
        count={campaign?.review_data.counts || 0}
        onClick={onClick}
      />
      <Box flex alignItems="center" sx={{ marginTop: '8px', gap: '6px' }}>
        <Heading3 className="text-orange">{campaign?.discount_rate}% </Heading3>
        <Heading2>{campaign?.price.toLocaleString() + '원'}</Heading2>
        <Heading3
          className="text-gray-300"
          style={{ fontWeight: 300, textDecoration: 'line-through' }}
        >
          {campaign?.original_price.toLocaleString()}원
        </Heading3>
      </Box>

      {campaign.discount_info && (
        <S.DiscountBox>
          <div className="discount-icon" />
          <p>
            {campaign.discount_info?.min_price_for_discount.toLocaleString()}원 이상 구매 시{' '}
            <strong>{campaign.discount_info?.discount_price.toLocaleString()}원</strong> 할인
          </p>
        </S.DiscountBox>
      )}
      <S.ProductDivider />
      <Accordion firstRow={<Paragraph1 fontWeight="bold">배송 정보</Paragraph1>} open>
        <InnerBox>
          <Box flex>
            <SmallText className="text-bold" style={{ marginRight: '12px', width: '32px' }}>
              배송
            </SmallText>
            <Box flex flexDirection="column">
              <SmallText>
                {campaign.delivery_policy.shipping_fee
                  ? `${campaign.delivery_policy.shipping_fee.toLocaleString()}원`
                  : '무료 '}
                {campaign.delivery_policy.is_free_shipping_used
                  ? ` (${campaign.delivery_policy.min_price_for_free_shipping.toLocaleString()}원 이상 구매시
                무료배송)`
                  : ''}
              </SmallText>

              {campaign.delivery_policy.available_remote ? (
                <SmallText>
                  도서산간배송비{' '}
                  {campaign.delivery_policy.remote_shipping_fee
                    ? `${campaign.delivery_policy.remote_shipping_fee.toLocaleString()}원 `
                    : '무료 '}
                  {campaign.delivery_policy.discounted_remote_shipping_fee !== null
                    ? `(${campaign.delivery_policy.min_price_for_remote_discount.toLocaleString()}원 이상 구매시 도서산간배송비
                      ${
                        campaign.delivery_policy.discounted_remote_shipping_fee
                          ? campaign.delivery_policy.discounted_remote_shipping_fee.toLocaleString() +
                            '원'
                          : '무료'
                      })`
                    : ''}
                </SmallText>
              ) : (
                <SmallText>도서산간의 경우 배송불가</SmallText>
              )}
              {campaign.delivery_policy.available_jeju ? (
                <SmallText>
                  제주배송비{' '}
                  {campaign.delivery_policy.jeju_shipping_fee
                    ? `${campaign.delivery_policy.jeju_shipping_fee.toLocaleString()}원 `
                    : '무료 '}
                  {campaign.delivery_policy.discounted_jeju_shipping_fee !== null
                    ? `(${campaign.delivery_policy.min_price_for_jeju_discount.toLocaleString()}원 이상 구매시 제주배송비
                      ${
                        campaign.delivery_policy.discounted_jeju_shipping_fee
                          ? campaign.delivery_policy.discounted_jeju_shipping_fee.toLocaleString() +
                            '원'
                          : '무료'
                      })`
                    : ''}
                </SmallText>
              ) : (
                <SmallText>제주의 경우 배송불가</SmallText>
              )}
            </Box>
          </Box>
          {campaign.delivery_cut_off_time && (
            <Box flex>
              <SmallText className="text-bold" style={{ marginRight: '12px' }}>
                출발 예정
              </SmallText>
              <Box flex flexDirection="column">
                <SmallText>당일배송 기준 {campaign.delivery_cut_off_time} 출고</SmallText>
              </Box>
            </Box>
          )}
        </InnerBox>
      </Accordion>
      {(campaign.manufacture_date || campaign.expiration_date) && (
        <Accordion
          sx={{ marginBottom: '20px' }}
          removeBorder
          firstRow={<Paragraph1 fontWeight="bold">상품 상세</Paragraph1>}
          headerType="custom"
        >
          <InnerBox>
            <Box flex>
              <SmallText className="text-bold" style={{ marginRight: '12px' }}>
                제조일자
              </SmallText>
              <Box flex flexDirection="column">
                <SmallText>{campaign.manufacture_date}</SmallText>
              </Box>
            </Box>
            {campaign.expiration_date && (
              <Box flex>
                <SmallText className="text-bold" style={{ marginRight: '12px' }}>
                  유통기한
                </SmallText>
                <Box flex flexDirection="column">
                  <SmallText>{campaign.expiration_date}</SmallText>
                </Box>
              </Box>
            )}
          </InnerBox>
        </Accordion>
      )}
    </section>
  );
}
